import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Crown,
  Check,
  Zap,
  Shield,
  Loader2,
  Sparkles,
  ArrowRight,
  Star,
} from 'lucide-react';
import { subscriptionAPI } from '../services/api';
import useUserStore from '../store/userStore';
import { trackViewPricing, trackBeginCheckout, trackPurchase } from '../utils/analytics';

// Razorpay Key ID (from environment or hardcoded for test)
const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID || 'rzp_test_RvnxRXETisJXdR';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, fetchSubscriptionStatus, subscriptionStatus, setPremiumStatus } = useUserStore();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [plansRes] = await Promise.all([
        subscriptionAPI.getPlans(),
        fetchSubscriptionStatus(),
      ]);
      setPlans(plansRes.data.plans || []);

      // Track pricing page view
      trackViewPricing();
    } catch (error) {
      console.error('Error loading pricing data:', error);
      toast.error('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (planId) => {
    if (user?.is_premium) {
      toast.info('You already have an active premium subscription!');
      return;
    }

    try {
      setProcessing(true);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        return;
      }

      // Create order
      const orderRes = await subscriptionAPI.createOrder(planId);
      const order = orderRes.data;

      // Track checkout start (price in INR, not paise)
      trackBeginCheckout(Math.round(order.amount / 100));

      // Open Razorpay checkout
      const options = {
        key: order.key_id || RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Buildex',
        description: order.description || 'Premium Annual Subscription',
        order_id: order.order_id,
        handler: async (response) => {
          try {
            // Verify payment on backend
            const verifyRes = await subscriptionAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyRes.data.is_premium) {
              // Track successful purchase
              trackPurchase(response.razorpay_payment_id, Math.round(order.amount / 100));

              setPremiumStatus(true, verifyRes.data.expires_at);
              toast.success('Payment successful! Welcome to Premium!');
              navigate('/profile');
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: user?.username || '',
          email: user?.email || '',
        },
        theme: {
          color: '#6366f1',
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.error || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  const isPremium = user?.is_premium || subscriptionStatus?.is_premium;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-12">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/20 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Upgrade to Premium</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Unlock Your Full Potential
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Get unlimited AI evaluations, access to all challenges, and accelerate your system design journey.
          </p>
        </div>

        {/* Already Premium Banner */}
        {isPremium && (
          <div className="mb-8 p-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl text-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Crown className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-xl font-bold">You're a Premium Member!</h3>
                <p className="text-indigo-100">
                  {subscriptionStatus?.expires_at && (
                    <>Your subscription is active until {new Date(subscriptionStatus.expires_at).toLocaleDateString()}</>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Free Plan */}
          <div className="bg-slate-800 rounded-2xl border-2 border-slate-700 p-8">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-white">Free</h3>
              <p className="text-slate-400 mt-1">Get started with the basics</p>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">₹0</span>
              <span className="text-slate-400">/forever</span>
              <div className="text-sm text-slate-500 mt-1">No credit card required</div>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-slate-300">2 AI evaluations per day</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-slate-300">Access to starter challenges</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-slate-300">Basic component library</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-500 mt-0.5" />
                <span className="text-slate-300">Community support</span>
              </li>
            </ul>
            <button
              disabled
              className="w-full py-3 px-6 bg-slate-700 text-slate-400 font-semibold rounded-xl cursor-not-allowed"
            >
              Current Plan
            </button>
          </div>

          {/* Premium Plan */}
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-8 text-white relative overflow-hidden">
            {/* Popular badge */}
            <div className="absolute top-4 right-4">
              <div className="flex items-center gap-1 px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-sm font-semibold">
                <Star className="w-4 h-4" />
                Popular
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-yellow-400" />
                <h3 className="text-xl font-bold">Premium</h3>
              </div>
              <p className="text-indigo-200 mt-1">Everything you need to master system design</p>
            </div>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold">
                  ₹{plans.find(p => p.id === 'premium_annual')?.price
                    ? Math.round(plans.find(p => p.id === 'premium_annual').price / 100)
                    : 499}
                </span>
                <span className="text-indigo-200">/year</span>
              </div>
              <div className="text-sm text-indigo-200 mt-1">International cards accepted</div>
            </div>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>20 AI evaluations per day</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>5 AI evaluations per hour</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>Access to ALL challenges</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>Priority AI processing</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>No advertisements</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-green-400 mt-0.5" />
                <span>Early access to new features</span>
              </li>
            </ul>
            <button
              onClick={() => handleSubscribe('premium_annual')}
              disabled={processing || isPremium}
              className="w-full py-3 px-6 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-indigo-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPremium ? (
                'Already Premium'
              ) : (
                <>
                  Subscribe Now
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Feature Comparison */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-8">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Feature Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-4 px-4 font-semibold text-white">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-white">Free</th>
                  <th className="text-center py-4 px-4 font-semibold text-indigo-400">Premium</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                <tr>
                  <td className="py-4 px-4 text-slate-400">AI Evaluations / Day</td>
                  <td className="py-4 px-4 text-center text-white">2</td>
                  <td className="py-4 px-4 text-center font-semibold text-indigo-400">20</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-400">AI Evaluations / Hour</td>
                  <td className="py-4 px-4 text-center text-white">-</td>
                  <td className="py-4 px-4 text-center font-semibold text-indigo-400">5</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-400">Challenge Access</td>
                  <td className="py-4 px-4 text-center text-white">Starter Only</td>
                  <td className="py-4 px-4 text-center font-semibold text-indigo-400">All Challenges</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-400">Priority Processing</td>
                  <td className="py-4 px-4 text-center text-slate-500">-</td>
                  <td className="py-4 px-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-400">Ad-Free Experience</td>
                  <td className="py-4 px-4 text-center text-slate-500">-</td>
                  <td className="py-4 px-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-slate-400">Early Feature Access</td>
                  <td className="py-4 px-4 text-center text-slate-500">-</td>
                  <td className="py-4 px-4 text-center">
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <span className="text-sm">Secure Payment via Razorpay</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            <span className="text-sm">Instant Activation</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
